import { UpsertTaskInput, TaskItemInput, OcaTemplate } from '@trnsact/trnsact-shared-types';
import { assigneeKinds, taskStatuses } from 'constants/tasks';
import {
  AssigneeKind,
  CreateTaskInputParams,
  LoadedTaskAssigneeSource,
  SpecialTaskItemType,
  SpecialTaskItemTypeConfig,
  TaskAssigneeOption,
  TaskItemFormValue,
  UpdateTaskInput,
  UpdateTaskInputParams,
  UploadDocumentContext,
} from '../types';
import { specialTaskItemTypesConfig } from '../constants';

const INSURANCE_VERIFICATION_EMAIL_TEMPLATE_ID = 'd-da8bec3016f544efa0a78d6ae339ef37';

const specialTaskItemTypes = Object.keys(specialTaskItemTypesConfig) as SpecialTaskItemType[];

const isSpecialTaskItemType = (type?: string | null): type is SpecialTaskItemType =>
  !!type && specialTaskItemTypes.includes(type as SpecialTaskItemType);

export const getSpecialTaskItemTypeConfig = (type?: string | null): SpecialTaskItemTypeConfig | undefined =>
  isSpecialTaskItemType(type) ? specialTaskItemTypesConfig[type] : undefined;

export const getSpecialTaskItemType = (taskItems: TaskItemFormValue[]): SpecialTaskItemType | null =>
  taskItems.map(taskItem => taskItem.type).find(isSpecialTaskItemType) ?? null;

export const getAvailableSpecialTaskItemTypes = (ocaTemplate?: OcaTemplate | null): SpecialTaskItemType[] =>
  ocaTemplate ? specialTaskItemTypes.filter(type => !!specialTaskItemTypesConfig[type].getOcaSection(ocaTemplate)) : [];

export const getAssigneeKind = (
  assignee?: TaskAssigneeOption | null,
  loadedTask?: LoadedTaskAssigneeSource | null
): AssigneeKind => {
  if (assignee?.isTradeReference) return assigneeKinds.tradeReference;
  if (assignee?.isInsuranceReference) return assigneeKinds.insuranceReference;
  if (assignee?.isApplicant) return assigneeKinds.applicant;

  // Assignee is resolved from the options list, which may still be loading in edit mode
  if (!assignee && loadedTask?.reference) return assigneeKinds.tradeReference;
  if (!assignee && loadedTask?.ownerPgId) return assigneeKinds.applicant;

  return assigneeKinds.user;
};

export const isReferenceAssigneeKind = (kind: AssigneeKind) =>
  kind === assigneeKinds.tradeReference || kind === assigneeKinds.insuranceReference;

export const getRegardingForAssigneeKind = (kind: AssigneeKind, defaultRegarding: string) => {
  switch (kind) {
    case assigneeKinds.tradeReference:
      return 'Trade Reference';
    case assigneeKinds.insuranceReference:
      return 'Insurance Verification';
    default:
      return defaultRegarding;
  }
};

export const resolveCompletedDateTime = (status: string, completedDateTime?: string | null) => {
  if (status !== taskStatuses.complete) return null;
  return completedDateTime ?? new Date().toISOString();
};

export const mapTaskItemsToInput = (taskItems: TaskItemFormValue[]): TaskItemInput[] =>
  taskItems.map(taskItem => {
    const isSpecialItem = isSpecialTaskItemType(taskItem.type);

    return {
      type: taskItem.type,
      docType: isSpecialItem ? null : taskItem.docType,
      documentId: isSpecialItem ? null : taskItem.documentId,
      taskAssigneeNotes: taskItem.taskAssigneeNotes,
      taskAssignorNotes: taskItem.taskAssignorNotes,
    };
  });

export const collectCreateTaskInput = ({
  account,
  assignee,
  assigneeKind,
  creditApplication,
  creditSubId,
  creditSubmission,
  specialTaskItemType,
  taskItems,
  userProfile,
  values,
  vendorOpportunityId,
}: CreateTaskInputParams): UpsertTaskInput => {
  const commonInput: UpsertTaskInput = {
    vendorOpportunityId,
    regarding: values.regarding,
    status: values.status,
    priority: values.priority,
    dueDateTime: values.dueDateTime,
    notifyByEmail: values.notifyByEmail,
    notifyAssignorOnReminder: values.notifyAssignorOnReminder,
    notificationContactIds: values.notificationContactIds,
    createdBy: userProfile.fullName,
    assignedByUser: userProfile.fullName,
    assignedByUserProfileId: userProfile.id,
    assignedByAccount: account.name,
    assignedByAccountId: account.id,
    taskItems: mapTaskItemsToInput(taskItems),
  };

  if (assigneeKind === assigneeKinds.tradeReference) {
    const tradeReference = creditApplication?.references?.find(r => r.referenceId === assignee.referenceId);

    return {
      ...commonInput,
      assignedToUser: tradeReference?.contactName || tradeReference?.companyName || 'Reference',
      assignedToAccount: 'Reference',
      assignedToEmails: [assignee.email ?? null],
      referenceId: assignee.referenceId,
    };
  }

  if (assigneeKind === assigneeKinds.insuranceReference) {
    const insuranceBroker = creditApplication?.references?.find(r => r.referenceId === assignee.referenceId);

    return {
      ...commonInput,
      creditApplicationId: creditApplication?.id,
      creditSubId: creditSubmission?.creditSubId,
      assignedToAccountId: creditSubmission?.lenderContactInfo?.[0]?.accountId,
      assignedToUser: insuranceBroker?.contactName || insuranceBroker?.companyName,
      assignedToEmails: [insuranceBroker?.contactEmail ?? null],
      emailTemplateId: INSURANCE_VERIFICATION_EMAIL_TEMPLATE_ID,
    };
  }

  const assignedInput: UpsertTaskInput = {
    ...commonInput,
    notifyByText: values.notifyByText,
    assignedToUser: assignee.fullName,
    assignedToUserProfileId: assignee.id,
    assignedToAccountId: account.id,
    assignedToAccount: 'will_be_changed_by_backend',
    assignedToApplicant: assignee.ownerPgId ? true : null,
    ownerPgId: assignee.ownerPgId,
    creditSubId: creditSubId || null,
  };

  if (!specialTaskItemType) return assignedInput;

  return {
    ...assignedInput,
    regarding: specialTaskItemTypesConfig[specialTaskItemType].regarding,
    assignedBySystem: false,
    assignedToUserProfileId: null,
    creditApplicationId: creditApplication?.id,
    dynamicsCreditApplicationId: creditApplication?.dynamicsId,
  };
};

export const collectUpdateTaskInput = ({
  account,
  assignee,
  completedDateTime,
  taskId,
  taskItems,
  uploadedDocumentIdByTaskItemId,
  values,
}: UpdateTaskInputParams): UpdateTaskInput => ({
  taskId,
  completedDateTime,
  status: values.status,
  priority: values.priority,
  dueDateTime: values.dueDateTime,
  notifyByEmail: values.notifyByEmail,
  notifyByText: values.notifyByText,
  notifyAssignorOnReminder: values.notifyAssignorOnReminder,
  notificationContactIds: values.notificationContactIds,
  assignedByAccount: account.name,
  assignedToUser: assignee.fullName,
  assignedToUserProfileId: assignee.id,
  taskItems: taskItems.map(taskItem => ({
    type: taskItem.type,
    docType: taskItem.docType,
    taskAssigneeNotes: taskItem.taskAssigneeNotes,
    taskAssignorNotes: taskItem.taskAssignorNotes,
    documentId:
      uploadedDocumentIdByTaskItemId[taskItem.taskItemId ?? ''] ?? taskItem.doc?.documentId ?? taskItem.documentId,
  })),
});

export const collectUploadDocumentInput = (taskItem: TaskItemFormValue, context: UploadDocumentContext) => {
  const invoiceQuoteNumber = taskItem.invoiceQuoteNumber ?? null;
  const needsQuoteNumberInDescription = taskItem.docType === 'invoice' || taskItem.docType === 'quote';

  return {
    vendorOpportunityId: context.vendorOpportunityId,
    creditAppId: context.creditAppId,
    docType: taskItem.docType,
    docDescription: needsQuoteNumberInDescription ? `${taskItem.docType}#${invoiceQuoteNumber}` : taskItem.docType,
    invoiceQuoteNumber,
    source: 'will_be_changed_by_server',
    docName: 'will_be_changed_by_server',
    createdBy: context.createdBy ?? 'will_be_changed_by_server',
  };
};
